import { createAction, props } from '@ngrx/store';
import { SearchQuery } from '../models/saved-search-query.interface';
import { CustomRange } from '../models/custom-range.interface';
import { TimeRangeOption } from '../../../../../shared/time-range';
import { Transaction } from '../../models/transaction.model';
import { ViewColumn } from '../models/saved-view.interface';
import { HttpResponse } from '@angular/common/http';
import { OverallWorkspaceTab } from '../../models/overall-workspace-tab.interface';
import { SavedTab } from '../models/saved-tab.interface';
import { ILinks, ILinksCreate, ILinksUpdate } from '../../models/links.interface';
import { Col } from '../../models/column.interface';
import { OverallBarChartItem } from '../models/overall-bar-chart-item.interface';

const featureName = 'Overall Workspace';

export const setSavedTabs = createAction(
  `[${featureName}] Set saved tabs`,
  props<{ tabs: SavedTab[] }>(),
);

export const readSavedTabs = createAction(
  `[${featureName}] Read saved tabs`,
);

export const writeSavedTabs = createAction(
  `[${featureName}] Write saved tabs`,
);

export const removeTab = createAction(
  `[${featureName}] Remove tab`,
  props<{ id: string }>(),
);

export const removeAllTabs = createAction(
  `[${featureName}] Remove all tabs`,
);

export const removeUnselectedTabs = createAction(
  `[${featureName}] Remove unselected tabs`,
);

export const renameTab = createAction(
  `[${featureName}] Rename tab`,
  props<{ id: string, name: string }>(),
);

export const setSelectedTabId = createAction(
  `[${featureName}] Set selected tab id`,
  props<{ selectedTabId: string }>(),
);

export const addDefaultTab = createAction(
  `[${featureName}] Add default tab`,
  props<{ query?: string }>(),
);

export const addFieldQueryTab = createAction(
  `[${featureName}] Add field query tab`,
  props<{ tabProps: Partial<OverallWorkspaceTab>, autoSearch?: boolean }>(),
);

export const addFieldQueryTabForCurrentTab = createAction(
  `[${featureName}] Add field query tab for current tab`,
  props<{ tabProps: Partial<OverallWorkspaceTab>, autoSearch?: boolean }>(),
);

export const addTabWithParams = createAction(
  `[${featureName}] Add tab with params`,
  props<{ tabProps: Partial<OverallWorkspaceTab> }>(),
);

export const setSavedQuery = createAction(
  `[${featureName}] Set saved query id`,
  props<{ query: SearchQuery, tabId: string }>(),
);

export const setSelectedViewId = createAction(
  `[${featureName}] Set selected view id`,
  props<{ viewId: string, tabId: string }>(),
);

export const setDefaultViewId = createAction(
  `[${featureName}] Set default view id`,
);

export const setDefaultViewAfterRemoving = createAction(
  `[${featureName}] Set default view after removing`,
  props<{ removedViewId: string, defaultViewId: string }>(),
);

export const setQuery = createAction(
    `[${featureName}] Set query`,
    props<{ query: string, tabId: string }>(),
);

export const resetQuery = createAction(
  `[${featureName}] Reset query`,
  props<{ tabId: string }>(),
);

export const setTimeRangeType = createAction(
  `[${featureName}] Set time range type`,
  props<{ tabId: string, timeRange: TimeRangeOption }>(),
);

export const setCustomRange = createAction(
  `[${featureName}] Set custom range`,
  props<{ tabId: string, customRange: CustomRange<Date> }>(),
);

export const setTransactions = createAction(
  `[${featureName}] Set transactions`,
  props<{ tabId: string, items: Transaction[]; totalItems: number; }>(),
);

export const setPartOfTransaction = createAction(
  `[${featureName}] Set part transactions`,
  props<{ tabId: string, items: Transaction[]; totalItems: number; }>(),
);

export const addSelectedItems = createAction(
  `[${featureName}] Add selected items`,
  props<{ tabId: string, addedItems: Transaction[] }>(),
);

export const removeSelectedItems = createAction(
  `[${featureName}] Remove selected items`,
  props<{ tabId: string, removedItems: Transaction[] }>(),
);

export const clearSelectedItems = createAction(
  `[${featureName}] Clear selected items`,
  props<{ tabId: string }>(),
);

export const setCurrentPage = createAction(
  `[${featureName}] Set current page`,
  props<{ tabId: string, currentPage: number; }>(),
);

export const setPageSize = createAction(
  `[${featureName}] Set page size`,
  props<{ tabId: string, pageSize: number }>(),
);

export const setSelectedColumns = createAction(
  `[${featureName}] Set selected columns`,
  props<{ tabId: string, selectedColumns: ViewColumn[] }>(),
);

export const resetSort = createAction(
  `[${featureName}] Reset grid sorting`,
  props<{ tabId: string }>(),
);

export const applySort = createAction(
  `[${featureName}] Apply grid sorting`,
  props<{ tabId: string }>(),
);

export const getTransactions = createAction(
  `[${featureName}] Get transactions`,
  props<{ tabId: string }>(),
);

export const fixTimeRange = createAction(
  `[${featureName}] Fix time range for tab`,
  props<{ tabId: string, fixedRange?: string; }>(),
);

export const getTransactionsFailure = createAction(
  `[${featureName}] Get transactions failure`,
  props<{ tabId: string }>(),
);

export const exportTransactions = createAction(
  `[${featureName}] Export transactions`,
  props<{ tabId: string, fields: string[] }>(),
);

export const exportTransactionsSuccess = createAction(
  `[${featureName}] Export transactions success`,
  props<{ tabId: string, file: HttpResponse<Blob> }>(),
);

export const exportTransactionsFailure = createAction(
  `[${featureName}] Export transactions failure`,
  props<{ tabId: string }>(),
);

export const openCompareTransactions = createAction(
  `[${featureName}] Open compare transactions`,
  props<{ tabId: string }>(),
);

export const closeCompareTransactions = createAction(
  `[${featureName}] Close compare transactions`,
  props<{ tabId: string }>(),
);

export const closeVariablesDetails = createAction(
  `[${featureName}] Close variables details if transaction`,
);

export const getTransactionsByIds = createAction(
  `[${featureName}] Request transactions by IDs`,
  props<{ eventIds: string[] }>(),
);

export const compareTransactions = createAction(
  `[${featureName}] Compare transactions`,
  props<{ eventIds: string[] }>(),
);

export const saveDataTransactionBuffer = createAction(
  `[${featureName}] Save transaction data`,
  props<{ eventId: string, data: Transaction }>(),
)

export const addTransactionsToBuffer = createAction(
  `[${featureName}] Add transactions to buffer`,
  props<{ transactions: { [key: string]: Transaction } }>(),
)

export const setScrollData = createAction(
  `[${featureName}] Set scroll data`,
  props<{ tabId: string, scrollIndex: number }>(),
);

export const updateTransactionsFields = createAction(
  `[${featureName}] Trigger columns set update`,
  props<{ columns: Col[] }>(),
);

export const updateTransactionsFieldsSuccess = createAction(
  `[${featureName}] Trigger columns set update success`,
);

export const updateTransactionsFieldsFailure = createAction(
  `[${featureName}] Trigger columns set update failure`,
);

export const requestFindConnectionData = createAction(
  `[${featureName}] Send Find Connection Request`,
  props<{ columns: Col[] }>(),
)

export const getUniqueUsersCountSuccess = createAction(
  `[${featureName}] Get unique users count success`,
  props<{ tabId: string, uniqueUsersCount: number }>(),
);

export const getUniqueUsersCountFailure = createAction(
  `[${featureName}] Get unique users count failure`,
  props<{ tabId: string }>(),
);

export const getLinks = createAction(
  `[${featureName}] Get link list`,
);

export const getLinksSuccess = createAction(
  `[${featureName}] Get link list success`,
  props<{ links: ILinks[] }>(),
);

export const getLinksFailure = createAction(
  `[${featureName}] Get link list failure`,
);

export const createLink = createAction(
  `[${featureName}] Create Link`,
  props<{ data: ILinksCreate }>(),
);

export const updateLink = createAction(
  `[${featureName}] Update link`,
  props<{ data: ILinksUpdate }>(),
);

export const deleteLink = createAction(
  `[${featureName}] Delete link`,
  props<{ id: string }>(),
);

export const cancelSearch = createAction(
  `[${featureName}] Cancel Search`,
  props<{ tabId?: string }>(),
);

export const cancelSearchSuccess = createAction(
  `[${featureName}] Cancel Search Success`,
);

export const getRiskRatingStatistics = createAction(
  `[${featureName}] Get risk rating statistics`,
);


export const getRiskRatingStatisticsSuccess = createAction(
  `[${featureName}] Get risk rating statistics success`,
  props<{ tabId?: string, statistic: OverallBarChartItem[] }>(),
);


export const getRiskRatingStatisticsFailure = createAction(
  `[${featureName}] Get risk rating statistics failure`,
);

export const resetColumnSorting = createAction(
  `[${featureName}] Reset column sorting`,
  props<{ tabId: string }>(),
);

export const clearStore = createAction(
  `[${featureName}] Clear store`,
);
